/**
 * 私有 OSS 图片：浏览器无法匿名访问存储直链，需通过 file 服务换取预签名 URL。
 */
import { generatePresignedUrl } from '@/api/system/file'

const presignedUrlCache = Object.create(null)

/** 桶内公开读目录（与 Nacos aliyun.oss.publicPrefixes 一致） */
export const OSS_PUBLIC_READ_PREFIXES = ['mall/']

function extractOssObjectPath(url) {
  try {
    return new URL(String(url).split('?')[0]).pathname.replace(/^\//, '')
  } catch (e) {
    const raw = String(url).split('?')[0]
    const idx = raw.indexOf('.aliyuncs.com/')
    return idx >= 0 ? raw.substring(idx + '.aliyuncs.com/'.length) : raw.replace(/^\//, '')
  }
}

export function isOssPublicReadUrl(url) {
  if (!url || typeof url !== 'string') return false
  const objectPath = extractOssObjectPath(url.trim())
  return OSS_PUBLIC_READ_PREFIXES.some((prefix) => objectPath.startsWith(prefix))
}

export function isAliyunOssHttpUrl(url) {
  if (!url || typeof url !== 'string') return false
  const u = url.trim()
  if (!/^https?:\/\//i.test(u)) return false
  return /\.aliyuncs\.com/i.test(u)
}

export async function resolveOssImageUrlForDisplay(originalUrl, options = {}) {
  const expirationMinutes = options.expirationMinutes != null ? options.expirationMinutes : 60

  if (!originalUrl || originalUrl === 'NULL' || originalUrl === 'null') {
    return ''
  }
  const url = String(originalUrl).trim()
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return url
  }

  const isHttp = /^https?:\/\//i.test(url)
  const base = process.env.VUE_APP_BASE_API || ''
  const fullUrl = isHttp ? url : base + (url.startsWith('/') ? url : '/' + url)

  if (!isAliyunOssHttpUrl(fullUrl)) {
    return fullUrl
  }

  if (isOssPublicReadUrl(fullUrl)) {
    return fullUrl.split('?')[0]
  }

  try {
    const u = new URL(fullUrl)
    const exp = u.searchParams.get('Expires') || u.searchParams.get('expires')
    if (exp) {
      const expMs = parseInt(exp, 10) * 1000
      if (!Number.isNaN(expMs) && expMs > Date.now() + 60 * 1000) {
        return fullUrl
      }
    }
  } catch (e) {
    /* ignore */
  }

  const cached = presignedUrlCache[fullUrl]
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url
  }

  try {
    const response = await generatePresignedUrl(fullUrl, expirationMinutes)
    if (response.code === 200 && response.url) {
      const expiresAt = response.expiresAt
        ? new Date(response.expiresAt).getTime()
        : Date.now() + Math.max(1, expirationMinutes - 5) * 60 * 1000
      presignedUrlCache[fullUrl] = { url: response.url, expiresAt }
      return response.url
    }
  } catch (error) {
    console.error('resolveOssImageUrlForDisplay: presign failed', error)
  }
  return fullUrl
}

export function clearOssImageUrlCache() {
  Object.keys(presignedUrlCache).forEach((k) => delete presignedUrlCache[k])
}
